import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Hash,
  ShieldCheck,
  Scale,
  CheckCircle2,
  AlertOctagon,
  KeyRound,
  Search,
  Handshake,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, type Variants } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { getLandingStats, type LandingStats } from '../services/api';
import { NumberCounter } from '../components/NumberCounter';
import { PageGlobe } from '../components/PageGlobe';

const EASE = [0.2, 0, 0, 1] as const;

// Full-bleed band: ``width: 100vw`` + ``margin-left: calc(-50vw + 50%)``
// breaks the section out of main's max-w-[1400px] container so its
// background spans the true viewport width, while the horizontal
// padding re-centres the content on the same 1400px column and never
// drops below the page gutter (1rem on phones, 1.5rem from ``sm``).
const FULL_BLEED = {
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  paddingLeft: 'max(calc(50vw - 700px + var(--page-gutter)), var(--page-gutter))',
  paddingRight: 'max(calc(50vw - 700px + var(--page-gutter)), var(--page-gutter))',
} as const;

// Reused stagger envelope.
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function HomePage() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => Boolean(s.token));
  const statsQuery = useQuery<LandingStats>({
    queryKey: ['landing-stats'],
    queryFn: getLandingStats,
    // 60s of staleness is plenty for a public marketing-page band; we
    // don't want the homepage hammering /public/landing-stats on every
    // route navigation.
    staleTime: 60_000,
  });

  return (
    <div className="relative">
      {/* Page-level wireframe globe — sits BEHIND every section so
          each section's bg decides whether it shows through. */}
      <PageGlobe />

      {/* ============================================================
          1. HERO — split layout, animated lookup demo on the right
          ============================================================
          Full-bleed: ``width: 100vw`` + ``margin-left: calc(-50vw +
          50%)`` makes the section span the TRUE viewport width,
          breaking out of main's max-w-[1400px] container. Without
          this the section ends at main's edge and the page bg shows
          through a vertical gutter on each side at viewports >1400px,
          creating a visible seam where the hero's soft gradient meets
          the bare paper bg. The inner content stays positioned
          correctly because we add ``px-6`` and a max-width wrapper
          inside.
       */}
      <section
        // ``-mt-8 sm:-mt-12`` pulls the band up flush against the
        // header by negating main's top padding (py-8 on phones, py-12
        // from ``sm``). Without it the section sits below the header,
        // showing a strip of bare paper between the header's bottom
        // rule and the hero's tinted background — a visible seam.
        className="relative -mt-8 sm:-mt-12 pt-8 pb-16 sm:pt-12 sm:pb-24 overflow-hidden"
        style={FULL_BLEED}
      >
        {/* Branded geometric background */}
        <BrandPattern />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center relative"
        >
          {/* Left — copy */}
          <div className="lg:col-span-6 relative z-10">
            <motion.div variants={fadeUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[var(--color-primary)]/25">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-primary-strong)]">
                  {t('home.eyebrow')}
                </span>
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-mono text-[clamp(2rem,7vw,4rem)] lg:text-[clamp(2.25rem,3.6vw,4rem)] leading-[1.02] tracking-[-0.02em] text-[var(--color-ink)] break-words"
            >
              {t('home.heroLine1')}
              <br />
              <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-strong)] bg-clip-text text-transparent">
                {t('home.heroLine2')}
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 max-w-[560px] text-[var(--color-ink-soft)] leading-[1.65] text-[16px]"
            >
              {t('home.lede')}
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-4 max-w-[560px] text-[var(--color-ink-soft)] leading-[1.65] text-[16px]"
            >
              {t('home.lede2')}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 sm:mt-10 flex items-center gap-3 flex-wrap">
              <Link
                to={isAuthenticated ? '/lookup' : '/login'}
                className="btn-primary group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 text-[15px] font-medium"
              >
                {isAuthenticated ? t('home.ctaLookup') : t('auth.signIn')}
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Link>
              <Link
                to={isAuthenticated ? '/entries/open' : '/signup-creditor'}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 text-[15px] text-[var(--color-ink)] hover:text-[var(--color-primary)] transition-colors"
              >
                {isAuthenticated
                  ? t('home.heroSecondaryCta')
                  : t('home.heroSecondaryCtaAnon')}
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
                  strokeWidth={2}
                />
              </Link>
            </motion.div>

            {/* small trust signals */}
            <motion.div
              variants={fadeUp}
              className="mt-10 sm:mt-12 flex items-center gap-x-6 gap-y-3 flex-wrap text-[12px] font-mono uppercase tracking-[0.12em] text-[var(--color-ink-muted)]"
            >
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
                {t('home.trustBadge1')}
              </span>
              <span className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.75} />
                {t('home.trustBadge2')}
              </span>
              <span className="flex items-center gap-2">
                <Scale className="w-3.5 h-3.5" strokeWidth={1.75} />
                {t('home.trustBadge3')}
              </span>
            </motion.div>
          </div>

          {/* Right — animated lookup demo */}
          <div className="lg:col-span-6 relative z-10">
            <LookupDemo />
          </div>
        </motion.div>
      </section>

      {/* ============================================================
          2. STATS BAND — big numbers on brand-blue surface
          ============================================================ */}
      <section
        className="relative py-12 sm:py-16 bg-[var(--color-primary)] text-white overflow-hidden"
        style={FULL_BLEED}
      >
        {/* Three stats: one per row on phones (a 2-column grid would
            orphan the third), three across from ``sm`` up. */}
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 relative">
          <StatBig
            label={t('home.statLenders')}
            count={
              statsQuery.data
                ? statsQuery.data.connected_lenders
                : undefined
            }
            isLoading={statsQuery.isLoading}
          />
          <StatBig
            label={t('home.statEntries')}
            count={
              statsQuery.data
                ? statsQuery.data.active_entries
                : undefined
            }
            isLoading={statsQuery.isLoading}
          />
          <StatBig
            label={t('home.statDebt')}
            value={
              statsQuery.data
                ? formatCompactDKK(statsQuery.data.total_debt)
                : undefined
            }
            unit="DKK"
            isLoading={statsQuery.isLoading}
          />
        </div>
      </section>

      {/* ============================================================
          3. HOW IT WORKS — 4-step horizontal rail
          ============================================================ */}
      <section className="py-16 sm:py-24">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="mb-10 sm:mb-14"
        >
          <motion.div variants={fadeUp} className="mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-primary-strong)]">
              {t('home.section2Eyebrow')}
            </span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="font-mono text-[clamp(1.75rem,3vw,2.5rem)] leading-[1.1] tracking-tight text-[var(--color-ink)] max-w-[680px]"
          >
            {t('home.section2Title')}
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--color-rule)] border border-[var(--color-rule)]"
        >
          <Step
            n="01"
            icon={Hash}
            term={t('home.step1Term')}
            body={t('home.step1Body')}
          />
          <Step
            n="02"
            icon={Search}
            term={t('home.step2Term')}
            body={t('home.step2Body')}
          />
          <Step
            n="03"
            icon={AlertOctagon}
            term={t('home.step3Term')}
            body={t('home.step3Body')}
          />
          <Step
            n="04"
            icon={Handshake}
            term={t('home.step4Term')}
            body={t('home.step4Body')}
          />
        </motion.div>
      </section>

      {/* ============================================================
          4. NARRATIVE SPLIT — meeting place between two parties
          ============================================================ */}
      <section
        className="relative py-16 sm:py-24 bg-[var(--color-paper-deep)]"
        style={FULL_BLEED}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="lg:col-span-5"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-primary-strong)]">
                {t('home.section3Eyebrow')}
              </span>
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="font-mono text-[clamp(1.75rem,3vw,2.5rem)] leading-[1.1] tracking-tight text-[var(--color-ink)]"
            >
              {t('home.section3Title')}
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="lg:col-span-7 space-y-6 text-[var(--color-ink-soft)] leading-[1.7] text-[16px]"
          >
            <motion.p variants={fadeUp}>{t('home.resolution1')}</motion.p>
            <motion.p variants={fadeUp}>{t('home.resolution2')}</motion.p>
            <motion.p variants={fadeUp}>{t('home.resolution3')}</motion.p>
            <motion.p variants={fadeUp}>{t('home.resolution4')}</motion.p>
            <motion.p variants={fadeUp}>{t('home.resolution5')}</motion.p>
            <motion.div variants={fadeUp} className="pt-4">
              <Link
                to={isAuthenticated ? '/min-sag' : '/login'}
                className="inline-flex items-center gap-2 text-[var(--color-primary)] hover:text-[var(--color-primary-strong)] font-medium text-[15px] group"
              >
                {t('home.section3Cta')}
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================================================
          4b. LENDERLY RECOMMENDATION — calm "Lexta anbefaler …"
          card. Uses Lenderly's own marketing copy verbatim and
          links the curious reader to /om-lenderly (internal sponsor
          page) as the primary destination, with a discreet external
          link to lenderly.eu for the impatient.
          ============================================================ */}
      <section className="py-16 sm:py-24">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="border border-[var(--color-rule)] bg-white px-5 sm:px-8 lg:px-12 py-8 sm:py-12 lg:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start"
        >
          <div className="lg:col-span-5">
            <motion.div variants={fadeUp} className="mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--color-primary-strong)]">
                {t('home.lenderlyEyebrow')}
              </span>
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="font-mono text-[clamp(1.5rem,2.5vw,2rem)] leading-[1.15] tracking-tight text-[var(--color-ink)]"
            >
              {t('home.lenderlyTitle')}
            </motion.h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[var(--color-ink-soft)] leading-[1.7] text-[15.5px]">
            <motion.p variants={fadeUp}>{t('home.lenderlyBody1')}</motion.p>
            <motion.p variants={fadeUp}>{t('home.lenderlyBody2')}</motion.p>
            <motion.div variants={fadeUp} className="pt-3 flex flex-wrap items-center gap-6">
              <Link
                to="/om-lenderly"
                className="inline-flex items-center gap-2 text-[var(--color-primary)] hover:text-[var(--color-primary-strong)] font-medium text-[15px] group"
              >
                {t('home.lenderlyCtaInternal')}
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Link>
              <a
                href="https://lenderly.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] font-mono text-[11px] uppercase tracking-[0.14em] transition-colors"
              >
                {t('home.lenderlyCtaExternal')}
              </a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ============================================================
          5. FINAL CTA — bold blue card
          ============================================================ */}
      <section className="py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: EASE }}
          className="relative overflow-hidden bg-[var(--color-blue-deep)] text-white px-5 sm:px-10 lg:px-16 py-10 sm:py-16 lg:py-20"
        >
          {/* large faint decorative shapes */}
          <div
            aria-hidden
            className="absolute -right-20 -top-20 w-[400px] h-[400px] rounded-full bg-[var(--color-primary)]/30 blur-3xl pointer-events-none"
          />
          <div
            aria-hidden
            className="absolute -left-32 -bottom-32 w-[320px] h-[320px] rounded-full bg-white/5 blur-3xl pointer-events-none"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
                {t('home.ctaEyebrow')}
              </span>
              <h2 className="mt-4 font-mono text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-tight">
                {t('home.ctaTitle')}
              </h2>
              <p className="mt-5 max-w-[560px] text-white/80 leading-[1.7] text-[16px]">
                {t('home.ctaBody')}
              </p>
            </div>
            <div className="lg:col-span-5 lg:justify-self-end flex items-center gap-3 flex-wrap">
              <Link
                to={isAuthenticated ? '/lookup' : '/login'}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-white text-[var(--color-blue-deep)] hover:bg-[var(--color-paper)] px-7 py-3.5 text-[15px] font-medium transition-colors group"
              >
                {isAuthenticated ? t('home.ctaLookup') : t('auth.signIn')}
                <ArrowRight
                  className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Link>
              <Link
                to="/lookup"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto border border-white/30 hover:border-white/80 text-white px-7 py-3.5 text-[15px] transition-colors"
              >
                {t('home.ctaSecondary')}
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}


// =====================================================================
// BrandPattern — subtle geometric grid + circles behind the hero.
// =====================================================================

function BrandPattern() {
  return (
    <svg
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none -z-10"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 1400 700"
    >
      <defs>
        <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
          <path
            d="M56 0H0V56"
            fill="none"
            stroke="var(--color-rule)"
            strokeWidth="0.5"
            opacity="0.5"
          />
        </pattern>
        <radialGradient id="fade" cx="60%" cy="40%" r="50%">
          <stop offset="0%" stopColor="var(--color-primary-soft)" stopOpacity="0.55" />
          <stop offset="60%" stopColor="var(--color-primary-soft)" stopOpacity="0.15" />
          <stop offset="100%" stopColor="var(--color-paper)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#fade)" />
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
}

// =====================================================================
// LookupDemo — the centerpiece of the hero. Auto-plays a CPR lookup
// every ~5 seconds: typewriter the CPR, "QUERYING REGISTER", then
// either CLEAR or HARD_BLOCK with hits. Loops.
// =====================================================================

const DEMO_CYCLES: Array<{
  cpr: string;
  state: 'clear' | 'block';
  hits: { name: string; lender: string; amount: string }[];
}> = [
  {
    cpr: '030380-1111',
    state: 'block',
    hits: [
      { name: 'A-Tenant Långiver', lender: 'LENDORA', amount: '12 500 DKK' },
      { name: 'B-Tenant Långiver', lender: 'LENDERLY', amount: '47 200 DKK' },
      { name: 'Externe Lån & Kredit', lender: 'EKSTERN', amount: '250 000 DKK' },
    ],
  },
  {
    cpr: '020280-5678',
    state: 'clear',
    hits: [],
  },
  {
    cpr: '040480-2222',
    state: 'block',
    hits: [{ name: 'B-Tenant Långiver', lender: 'LENDERLY', amount: '18 000 DKK' }],
  },
];

function LookupDemo() {
  const { t } = useTranslation();
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'querying' | 'result'>('typing');
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const target = DEMO_CYCLES[cycle].cpr;
    setTyped('');
    setPhase('typing');

    let i = 0;
    const typeTimer = setInterval(() => {
      i++;
      setTyped(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(typeTimer);
        setTimeout(() => setPhase('querying'), 500);
        setTimeout(() => setPhase('result'), 1700);
        setTimeout(() => setCycle((c) => (c + 1) % DEMO_CYCLES.length), 5800);
      }
    }, 70);

    return () => clearInterval(typeTimer);
  }, [cycle]);

  const current = DEMO_CYCLES[cycle];

  return (
    <div className="relative">
      {/* Decorative gradient blobs for depth */}
      <div
        aria-hidden
        className="absolute -inset-12 -z-10 pointer-events-none"
      >
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[var(--color-primary)]/20 blur-[80px]" />
        <div className="absolute bottom-0 left-8 w-[280px] h-[280px] rounded-full bg-[var(--color-primary)]/10 blur-[60px]" />
      </div>

      {/* Laptop chrome frame */}
      <div className="relative">
        {/* Top bezel with traffic-light dots — borders dropped; the
            wraparound shadow on the inner screen below carries the
            edge instead, so the right and left sides don't render
            a hard 1px line against the page background. */}
        <div className="rounded-t-xl bg-[#1a2238] px-4 py-2.5 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
          <div className="flex-1 mx-4 px-3 py-1 rounded-md bg-[#0c111d] text-white/40 text-[11px] font-mono tabular flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)]" />
            lexta.dk/lookup
          </div>
        </div>

        {/* Inner screen — wraparound shadow with horizontal spread on
            every side instead of a side-border + downward-only shadow.
            Removes the hard 1px line on the right + left edges of the
            card that read as visual seams against the page bg. */}
        <div className="relative bg-white rounded-b-xl shadow-[0_24px_48px_-12px_rgba(15,23,42,0.18),0_0_30px_-8px_rgba(15,23,42,0.08),0_8px_24px_-6px_rgba(24,137,205,0.12)] overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-[var(--color-rule)] bg-white">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[var(--color-primary)] flex items-center justify-center rounded">
              <Search className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--color-ink-muted)]">
              {t('home.demoTopBar')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {DEMO_CYCLES.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === cycle
                    ? 'bg-[var(--color-primary)]'
                    : 'bg-[var(--color-rule)]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="px-4 sm:px-6 pt-6 pb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-ink-muted)] mb-2">
            {t('home.demoCprLabel')}
          </div>
          <div className="font-mono text-2xl tracking-wide text-[var(--color-ink)] tabular flex items-center min-h-[36px]">
            {typed}
            {phase === 'typing' && (
              <span className="inline-block ml-1 w-2 h-6 bg-[var(--color-primary)] animate-pulse" />
            )}
          </div>
        </div>

        {/* State row */}
        <div className="px-4 sm:px-6 pb-2 min-h-[28px]">
          {phase === 'querying' && (
            <motion.div
              key="querying"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-primary)] flex items-center gap-2"
            >
              <span className="w-3 h-3 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" />
              {t('home.demoQuerying')}
            </motion.div>
          )}
        </div>

        {/* Result */}
        <div className="px-4 sm:px-6 pb-6 min-h-[200px]">
          {phase === 'result' && current.state === 'clear' && (
            <motion.div
              key={`clear-${cycle}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32 }}
              className="border-2 border-[var(--color-primary)] bg-[var(--color-primary-soft)] px-4 sm:px-5 py-5 flex items-center gap-4"
            >
              <CheckCircle2 className="w-8 h-8 text-[var(--color-primary)] flex-shrink-0" strokeWidth={1.75} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-primary-strong)]">
                  {t('home.demoVerdict')}
                </div>
                <div className="font-mono text-xl font-semibold text-[var(--color-ink)]">
                  {t('home.demoVerdictClear')}
                </div>
                <div className="text-[12px] text-[var(--color-ink-soft)] mt-0.5">
                  {t('home.demoClearBody')}
                </div>
              </div>
            </motion.div>
          )}

          {phase === 'result' && current.state === 'block' && (
            <motion.div
              key={`block-${cycle}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32 }}
            >
              <div className="border-2 border-[var(--color-alert)] bg-[var(--color-alert-soft)] px-4 sm:px-5 py-4 mb-3 flex items-center gap-4">
                <AlertOctagon className="w-7 h-7 text-[var(--color-alert)] flex-shrink-0" strokeWidth={1.75} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-alert)]">
                    {t('home.demoVerdict')}
                  </div>
                  <div className="font-mono text-xl font-semibold text-[var(--color-ink)]">
                    {t('home.demoVerdictBlock')}
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-[var(--color-rule)] border border-[var(--color-rule)]">
                {current.hits.map((h, i) => (
                  <motion.li
                    key={`${cycle}-${i}`}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.28, delay: 0.1 + i * 0.08 }}
                    className="px-4 py-2.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-[12px] font-medium text-[var(--color-ink)] truncate">
                        {h.name}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-[var(--color-ink-muted)]">
                        {h.lender}
                      </div>
                    </div>
                    <div className="font-mono text-[12px] font-semibold text-[var(--color-ink)] tabular shrink-0">
                      {h.amount}
                    </div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// StatBig — large numeric on the brand-blue stats band.
//
// Two render modes, mutually exclusive:
//
//   1. ``count`` (number) — animates from 0 → count via ``NumberCounter``
//      with Danish thin-space thousands grouping. Used for whole-number
//      stats: lenders / entries / lookups.
//   2. ``value`` (string) — pre-formatted display value, no animation.
//      Used for the compact-debt rendering ("328 200" or "1,5M") so we
//      don't have to teach ``NumberCounter`` to interpolate the "M"
//      suffix mid-tween.
//
// While the parent's react-query call is in flight (``isLoading``),
// the band shows a soft em dash so the layout doesn't pop in. After
// load, even a real zero renders as "0" — which is the honest answer
// for a fresh tenant with no data yet.
// =====================================================================

function StatBig({
  label,
  count,
  value,
  unit,
  suffix,
  isLoading,
}: {
  label: string;
  count?: number;
  value?: string;
  unit?: string;
  suffix?: string;
  isLoading?: boolean;
}) {
  const showCounter = count !== undefined;
  const showValue = value !== undefined && !showCounter;
  const showPlaceholder = !showCounter && !showValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: EASE }}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 mb-3">
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        {showCounter && (
          <NumberCounter
            value={count!}
            className="font-mono text-[clamp(2.25rem,5vw,4rem)] leading-none font-semibold tabular"
          />
        )}
        {showValue && (
          <span className="font-mono text-[clamp(2.25rem,5vw,4rem)] leading-none font-semibold tabular">
            {value}
          </span>
        )}
        {showPlaceholder && (
          <span
            className={`font-mono text-[clamp(2.25rem,5vw,4rem)] leading-none font-semibold tabular ${
              isLoading ? 'text-white/40' : 'text-white/60'
            }`}
            aria-busy={isLoading}
          >
            —
          </span>
        )}
        {unit && (
          <span className="font-mono text-base text-white/60">{unit}</span>
        )}
        {suffix && (
          <span className="font-mono text-base text-white/60">{suffix}</span>
        )}
      </div>
    </motion.div>
  );
}

// =====================================================================
// formatCompactDKK — debt-band display formatter.
//
// Under 1,000,000 → grouped whole DKK with thin-space separators
// ("328 200"). At or above 1M → single decimal with Danish comma and
// "M" suffix ("1,5M"). Matches the screenshot's editorial style and
// avoids running off the end of the band's column on a 250M+ figure.
// =====================================================================

function formatCompactDKK(amount: number): string {
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) {
    const m = amount / 1_000_000;
    // One decimal, rounded; Danish decimal comma. "1,5M" rather than
    // "1.5M". Strip trailing ",0" so a clean 2M reads "2M" not "2,0M".
    const oneDecimal = (Math.round(m * 10) / 10).toFixed(1).replace('.', ',');
    return oneDecimal.endsWith(',0')
      ? `${oneDecimal.slice(0, -2)}M`
      : `${oneDecimal}M`;
  }
  return Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// =====================================================================
// Step — one card in the 4-up "how it works" rail.
// =====================================================================

function Step({
  n,
  icon: Icon,
  term,
  body,
}: {
  n: string;
  icon: typeof ShieldCheck;
  term: string;
  body: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="bg-white p-7 group hover:bg-[var(--color-primary-soft)]/40 transition-colors"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <span className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--color-ink-muted)] tabular">
          {n}
        </span>
      </div>
      <h3 className="font-mono text-[15px] font-semibold tracking-tight text-[var(--color-ink)] mb-2">
        {term}
      </h3>
      <p className="text-[13.5px] text-[var(--color-ink-soft)] leading-[1.6]">
        {body}
      </p>
    </motion.div>
  );
}

